﻿/* global jQuery */

import { QueryStringAsObject, AsyncUtils } from '../main/ObjectUtils.js';
import { PollingStatusMonitor } from './StatusMonitor.js';

//#region ************* Helper functions ***************

function splitUrl(url) {
  //console.log(typeof url, url);
  if (!url) {
    return null;
  }
  const tupple = {
    origin: null,
    path: null
  };
  const a = document.createElement('a');
  a.href = url;

  tupple.origin = a.origin;
  if (typeof a.origin === 'undefined') { // IE
    tupple.origin = a.hostname;
    const ndx = url.indexOf('//');
    if (ndx > 0) {
      tupple.origin = url.substring(0, ndx + 2) + tupple.origin;
    }
  }
  tupple.path = a.pathname + a.search;
  return tupple;
}

// No longer used, but we'll keep it for now due to its elegance
function updateQueryStringParameter(url, key, value) {
  const regExp = new RegExp('([?&])' + key + '=.*?(&|$)', 'i');
  const separator = url.indexOf('?') !== -1 ? '&' : '?';
  if (url.match(regExp)) {
    return url.replace(regExp, '$1' + key + '=' + value + '$2');
  } else {
    return url + separator + key + '=' + value;
  }
}

//#endregion Helper functions

const DesignerUserTypes = {
  endUser: 'EndUser',
  reseller: 'Reseller',
  langlo: 'Langlo'
}

/* The successful progression is:
 *
 * A) During install: NotInstalled -> Downloaded -> Installing -> Installed -> AppLaunched -> AppStarted (2/15/2024: AppStarted not yet implemented inside Designer app)
 * B) During target launch: (undefined) -> BootstrapStarted -> AppLaunched -> AppStarted (see above re. AppStarted)
 */
export const BootstrapStatuses = Object.freeze({
  NotInstalled: 'NotInstalled',
  Downloaded: 'Downloaded', // The bootstrap exe has been downloaded, but not started yet
  Installing: 'Installing', // The bootstrap has started from the download folder
  Installed: 'Installed', // The bootstrap app has been installed
  BootstrapStarted: 'BootstrapStarted', // The bootstrap app has started from its permanent location. Corresponds to "Installing"
  AppLaunched: 'AppLaunched', // The bootstrap app has launched the target app (e.g. Designer)
  AppStarted: 'AppStarted' // The target app has started
});
export const BootstrapStatusValues = Object.freeze({
  NotInstalled: 0,
  Downloaded: 1, // The bootstrap exe has been downloaded, but not started yet
  Installing: 2, // The bootstrap has started from the download folder
  Installed: 3, // The bootstrap app has been installed
  BootstrapStarted: 4, // The bootstrap app has started from its permanent location
  AppLaunched: 5, // The bootstrap app has launched the target app (e.g. Designer)
  AppStarted: 6 // The target app has started
});
export class BootstrapStatus {
  /**
   * We consider the Bootstrap app installed if the status is anything but NotInstalled or Downloaded.
   * @returns False if status does not have a value, or if it's equal to NotInstalled or Downloaded.
   * Otherwise returns true.
   */
  static isInstalled(status) {
    return status && !(status === BootstrapStatuses.NotInstalled || status === BootstrapStatuses.Downloaded);
  }
}

export class DesignerAppLauncher {
  constructor(designerAppService, adminService, sessionStorageMgr, telemetrySession, config) {
    this.designerAppService = designerAppService;
    this.adminService = adminService;
    this.sessionStorageMgr = sessionStorageMgr;
    this.telemetrySession = telemetrySession;
    this.isFirstStart = true;
    this.pushDataLayerEvent = config.pushDataLayerEvent;
    this.isEndUser = config.isEndUser;
    this.userType = config.userType;
    this.appId = config.appId;
    this.appCulture = config.appCulture;
    this.testErrorPopup = config.testErrorPopup;
    this.loginKey = config.loginKey;
    this.loginKeyExpirationUtc = config.loginKeyExpirationUtc;

    this.LangloDesignerUriScheme = 'langlodesignerV4:';
    this.POLLING_CANCELLED = 'POLLING_CANCELLED';

    // Declaring our additional state here helps seeing what state is available:
    this.sessionId = null; // Is reset every time we start a download or a launch
    this.monitor = null;
    this.updateAppUrl(null); // Resets this.appUrl, this.fullDesignerClickOnceUrl, this.bootstrapWebApiUrl, this.langloWebDomain
    this.hasGottenFirefoxNetworkError = false; // Is reset each time we start monitoring bootstrap status changes
    this.currentBootstrapStatus = null; // Is reset each time we start monitoring
    this.largestKnownBootstrapStatus = this.readBootstrapStatusFromLocalStorage(); // Is updated and save to localStorage later
  }

  //#region Bootstrap Status Management

  readBootstrapStatusFromLocalStorage() {
    const statusStr = localStorage.getItem('DesignerBootstrapStatus');
    return statusStr ? JSON.parse(statusStr) : BootstrapStatuses.NotInstalled;
  }

  writeBootstrapStatusToLocalStorage(status) {
    if (status) {
      localStorage.setItem('DesignerBootstrapStatus', JSON.stringify(status));
    } else {
      localStorage.removeItem('DesignerBootstrapStatus');
    }
  }

  /**
   * Sets this.currentBootstrapStatus and updates this.largestKnownBootstrapStatus and localStorage,
   * but only if the new status is larger than this.largestKnownBootstrapStatus.
   */
  updateBootstrapStatus(newStatus) {
    if (newStatus !== this.currentBootstrapStatus) {
      this.currentBootstrapStatus = newStatus;
      const newStatusValue = BootstrapStatusValues[newStatus];
      const largestKnownStatusValue = BootstrapStatusValues[this.largestKnownBootstrapStatus];
      if (newStatusValue > largestKnownStatusValue) {
        this.largestKnownBootstrapStatus = newStatus;
        this.writeBootstrapStatusToLocalStorage(this.largestKnownBootstrapStatus);
      }
    }
  }

  /**
   * We consider the Bootstrap app installed if the largestKnownBootstrapStatus is anything but
   * NotInstalled or Downloaded.
   * @returns False if status does not have a value, or if it's equal to NotInstalled or Downloaded.
   * Otherwise returns true.
   */
  isBootstrapInstalled() {
    return BootstrapStatus.isInstalled(this.largestKnownBootstrapStatus);
  }

  //#endregion Bootstrap Status Management

  /**
   * We look for the preferred customer data in three places, in order of precedence:
   * 1. This page's query string.
   * 2. The referring page's query string.
   * 3. The session storage (in case the user got to this page, then moved around the site and came back here again)
   */
  getPreferredCustData() {
    /* NOTE: If we end up making this method async, then we need to protect against re-entrant access
     * to the sessionStorage. See the DesignerTelemetrySession.acquireSessionAsync() method as well as
     * AsyncSemaphore class for more on how to do this. */
    const storage = this.sessionStorageMgr.getStorage();
    const queryStrAsObject = new QueryStringAsObject();
    let query = queryStrAsObject.query;
    let preferredCustNo = query.preferred_custno;
    let preferredGroupId = query.preferred_groupid;
    if (preferredCustNo || preferredGroupId) {
      storage.preferredCustData = { preferredCustNo, preferredGroupId };
    } else {
      query = queryStrAsObject.parseFullUrl(document.referrer);
      preferredCustNo = query.preferred_custno;
      preferredGroupId = query.preferred_groupid;
      if (preferredCustNo || preferredGroupId) {
        storage.preferredCustData = { preferredCustNo, preferredGroupId };
      }
    }
    this.sessionStorageMgr.save();
    return storage.preferredCustData || {};
  }

  updateAppUrl(appUrl) {
    this.appUrl = appUrl;
    let { origin: langloWebDomain, path: clickOncePath } = splitUrl(appUrl) || { origin: null, path: null };
    if (langloWebDomain && !langloWebDomain.endsWith('/')) {
      langloWebDomain += '/';
    }
    if (clickOncePath && clickOncePath.startsWith('/')) {
      clickOncePath = clickOncePath.slice(1);
    }
    this.fullDesignerClickOnceUrl = langloWebDomain + clickOncePath;
    this.bootstrapWebApiUrl = langloWebDomain + 'DesignerBootstrap/Bootstrap.aspx/';
    this.langloWebDomain = langloWebDomain;
  }

  isLoginKeyExpired() {
    /*
    const expirationDateTimeUtc = new Date(this.loginKeyExpirationUtc);
    // Convert currentDateTime to UTC for comparison (60000 is ms per minute):
    const currentDateTime = new Date(); // This is in the client's local time
    const utcMs = currentDateTime.getUTCMilliseconds();
    const currentDateTime2 = new Date(utcMs);
    const timeMs = currentDateTime.getTime(); // Time in ms since midnight 1/1/1970 UTC
    const offsetMin = currentDateTime.getTimezoneOffset(); // Difference between UTC and local time in minutes
    const offsetMs = offsetMin * 60000;
    var currentDateTimeUtc = new Date(timeMs + offsetMs);
    return currentDateTimeUtc > expirationDateTimeUtc;
    */
    const expirationDateTime = new Date(this.loginKeyExpirationUtc); // The Date constructor converts the UTC parameter to a local time (with timezone offset)
    const currentDateTime = new Date(); // This is in the client's local time, s/b compatible with expirationDateTime
    return currentDateTime > expirationDateTime;
  }

  /**
   * Updates this.loginKey and this.loginKeyExpirationUtc with a new key retrieved from the Admin Service.
   * Note, this method swallows all exceptions to allow the app launch to continue.
   */
  async getNewLoginKeyAsync() {
    try {
      const keyAndExpiration = await this.adminService.createLoginKey();
      this.loginKey = keyAndExpiration.loginKey;
      this.loginKeyExpirationUtc = keyAndExpiration.expiration;
    } catch (errorMsg) {
      console.log('Got error:');
      console.log(errorMsg);
    }
  }

  async updateAppUrlAsync() {
    // langloDevWebDomain: 'http://local.link.langlo.no/',
    // langloStagingWebDomain: 'http://linkdev.langlo.no/',
    // langloLiveWebDomain: 'http://link.langlo.no/',
    // designerClickOncePath: 'Designer3/DesignerDev.application',
    // queryString: '?Lang=nb-NO'

    // This copies the logic in LinkApplication's DoParseActivationUri() method:
    const requestedUserType = this.isEndUser ? DesignerUserTypes.endUser : DesignerUserTypes.reseller;

    const { preferredCustNo, preferredGroupId } = this.getPreferredCustData();
    let appUrl = await this.designerAppService.getFullAppUrlAsync(this.appId, this.appCulture, preferredCustNo,
      preferredGroupId, requestedUserType, this.telemetrySession.sessionId);
    if (this.loginKey) {
      if (this.isLoginKeyExpired()) {
        await this.getNewLoginKeyAsync();
      }
      /* Warning: "Key" has to be capitalized like this due to a weakness in Designer.
       * This will be fixed in the next release of Designer (after 2/22/2024). */
      const loginStr = `Key=${this.loginKey}`;
      appUrl += appUrl.includes('?') ? '&' + loginStr : '?' + loginStr;
    }
    this.updateAppUrl(appUrl);
  }

  /**
   * Uses the Designer service web api to download the Bootstrap application, but does not trigger
   * the browser download.
   * @returns The URL to the blob data for the downloaded app.
   */
  async downloadBootstrapAppDataAsync() {
    /* This approach bypasses issues (CORS, Windows popups) related to downloading the app file
     * via an href to the file on the link.langlo.no server. Instead we download the file contents
     * via a web api call and use blob features to save the file contents. 1/22/2024. */
    const blob = await this.designerAppService.downloadBootstrapAppAsync();
    const blobUrl = URL.createObjectURL(blob);
    return blobUrl;
  }

  /**
   * Uses the browser DOM to trigger a download of the data at the url parameter.
   * @param {any} url The resource to download
   * @param {any} filename The filename to save the resource as in the local filesystem.
   */
  triggerBrowserDownload(url, filename) {
    // Create a temporary anchor element and trigger download
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = filename;
    document.body.appendChild(anchor);
    try {
      anchor.click();
    } finally {
      document.body.removeChild(anchor);
    }
  }

  getfullDesignerClickOnceUrl() {
    var fakeTheRealSite = false;
    if (fakeTheRealSite) {
      // This statement ensure that we ALWAYS load the live Designer if it's the one from LinkDev
      // that is attempted loaded:
      return this.fullDesignerClickOnceUrl
        .toLowerCase()
        .replace('linkdev.', 'link.')
        .replace('designerdev.', 'designer.');
    }
    return this.fullDesignerClickOnceUrl;
  }

  getSiteId(url) {
    url = url.toLowerCase();
    if (url.includes('local.link.langlo.no'))
      return 'D';
    if (url.includes('link.langlo.no'))
      return 'L';
    if (url.includes('linkdev.langlo.no'))
      return 'S';
    return 'L';
  }

  /**
   * Uses window.location = uri to navigate to our custom uri scheme. Code after this method call may
   * or may not execute, depending on how long it takes the browser to do the navigation.
   */
  navigateToBootstrapAppUrl(sessionId) {
    console.log("Starting bootstrap app, first let's get the URI...");
    const clickOnceUrl = this.getfullDesignerClickOnceUrl();
    /* By navigating to our custom URI scheme we trigger Windows to start the bootstrap app.
       We pass the ClickOnce app's URI as a parameter to the custom URI. This will
       become the command line for the the bootstrap app. */
    const uri = `${this.LangloDesignerUriScheme}${clickOnceUrl}&session=${sessionId}`;
    console.log(`Navigating to ${uri}...`);
    window.location = uri;
  }

  /** Send "designer_start_attempted" event to Google Tag Manager (GTM). */
  raiseStartingDesignerEvent() {
    if (this.pushDataLayerEvent) {
      window.dataLayer.push({
        event: 'designer_start_attempted',
        app_id: this.appId,
        enduser_mode: this.isEndUser, // True if we're starting the app in end-user-mode
        actual_user_type: this.userType // The actual user type taken from the currently logged in user
      });
    }
  }

  /**
   * Launches the app by making an assignment to window.location. Before it does that, several async
   * calls are awaited. Code after the call to this method is not guaranteed to execute.
   */
  async launchAppAsync() {
    await this.updateAppUrlAsync(); // If this one fails, then we have no choice but to give up (we need the url's)
    this.raiseStartingDesignerEvent();
    if (this.testErrorPopup) {
      throw new Error('Testing the display of error messages...');
    }
    this.isFirstStart = false;
    if (this.deviceTestResults.useBootstrapToStartApp) { // We can use our Bootstrap program on Chrome, Firefox, and Edge, on Windows.
      /* I belive we want to start a new session everytime. Otherwise, if the user clicks the start
        * button for a second time, he would get the Bootstrap status of the previous session.
        * 2/15/2024. */
      //if (!this.sessionId) {
      //  this.sessionId = await this.designerAppService.createBootstrapSession();
      //}
      this.sessionId = await this.designerAppService.createBootstrapSession();

      this.startMonitoringBootstrapStatus(); // Might already been done, if so, it doesn't do anything
      /* Uses window.location = uri to navigate to our custom uri scheme. Code after this call may
       * or may not execute, depending on how long it takes the browser to do the navigation. */
      this.navigateToBootstrapAppUrl(this.sessionId);
    } else {
      // Try to start the app by navigating to it. If this isn't supported (ClickOnce not supported),
      // then the browser will probably try to download the application file instead.
      const fakeTheRealSite = false;
      const url = fakeTheRealSite
        ? this.appUrl.toLowerCase().replace('linkdev.', 'link.').replace('designerdev.', 'designer.')
        : this.appUrl;
      window.location = url;
    }
  }

  /**
   * Downloads the bootstrap app using Langlo-Designer-{siteId{sessionId}.exe as the filename.
   */
  async downloadAppAsync() {
    await this.updateAppUrlAsync(); // If this one fails, then we have no choice but to give up (we need the url's)
    this.raiseStartingDesignerEvent();
    this.isFirstStart = false;

    /* I belive we want to start a new session everytime. Otherwise, if the user clicks the start
      * button for a second time, he would get the Bootstrap status of the previous session.
      * 2/15/2024. */
    //if (!this.sessionId) {
    //  let clickOnceUrl = this.getfullDesignerClickOnceUrl();
    //  this.sessionId = await this.designerAppService.createBootstrapSession(clickOnceUrl);
    //}
    let clickOnceUrl = this.getfullDesignerClickOnceUrl();
    this.sessionId = await this.designerAppService.createBootstrapSession(clickOnceUrl);

    /* This approach bypasses issues (CORS, Windows popups) related to downloading the app file
     * via an href to the file on the link.langlo.no server. Instead we download the file contents
     * via a web api call and use blob features to save the file contents. 1/22/2024. */
    const blobUrl = await this.downloadBootstrapAppDataAsync();
    try {
      this.startMonitoringBootstrapStatus();
      const siteId = this.getSiteId(this.langloWebDomain);
      const filename = `Langlo-Designer-${siteId}${this.sessionId}.exe`;
      this.triggerBrowserDownload(blobUrl, filename);
      /* This is an internal status change, not received from the web API. And, we don't need to
       * dispatch this event (any owner process step should do that when this method returns).
       * The call sets this.currentBootstrapStatus and updates this.largestKnownBootstrapStatus and
       * localStorage, but only if the new status is larger than this.largestKnownBootstrapStatus. */
      this.updateBootstrapStatus(BootstrapStatuses.Downloaded);
    } finally {
      window.URL.revokeObjectURL(blobUrl);
    }
  }

  startMonitoringBootstrapStatus() {
    if (!this.monitor) {
      //#region Event Handlers

      const handleGetStatusAsync = async monitor => {
        const status = await this.designerAppService.getBootstrapSessionStatus(this.sessionId);
        if (!status) {
          /* When the server returns null, undefined, or an empty string, it means that the Bootstrap
           * session has not received a status from the Bootstrap app yet. We, on the client side, may
           * already have a status like 'NotInstalled' or 'Downloaded', and in those cases we don't
           * want to replace that status with the empty one. */
          if (this.monitor.priorStatus === BootstrapStatuses.NotInstalled || this.monitor.priorStatus === BootstrapStatuses.Downloaded) {
            status = this.monitor.priorStatus; // Causes the monitor to say that status has not changed
          }
        }
        return status;
      };

      const handleStatusChanged = monitor => {
        const oldStatus = monitor.priorStatus;
        const newStatus = monitor.status;
        console.log(`DesignerAppLauncher: received bootstrap status change, old status: ${oldStatus}, new status: ${newStatus}, user data: ${monitor.userData}`);
        /* Sets this.currentBootstrapStatus and updates this.largestKnownBootstrapStatus and localStorage,
         * but only if the new status is larger than this.largestKnownBootstrapStatus. */
        this.updateBootstrapStatus(newStatus);
        if (this.onBootstrapStatusChanged) {
          this.onBootstrapStatusChanged(monitor); // The StartDesignerProcess listens for the event and dispatches to process steps
        }
      };

      const handleIsEqualStatus = monitor => {
        if (!monitor.priorStatus && !monitor.status) {
          return true;
        }
        return monitor.status === monitor.priorStatus;
      }

      const handleError = (monitor, error) => {
        console.error(`DesignerAppLauncher: received error while monitoring bootstrap status: ${error.message}`);
        if (this.onBootstrapStatusMonitorError) {
          /* This is a workaround for an issue in Firefox (latest version) where the first fetch
           * call to get the Bootstrap status fails. Subsequent calls succeed. 2/17/2024. */
          if (!this.hasGottenFirefoxNetworkError && error instanceof TypeError && this.deviceTestResults.isFirefox) {
            this.hasGottenFirefoxNetworkError = true;
          } else {
            this.onBootstrapStatusMonitorError(monitor, error);
          }
        }
      }

      //#endregion Event Handlers

      // Monitor for 10 minutes with 2 seconds interval between each poll:
      const duration = 10 * 60 * 1000;
      const interval = 2 * 1000;
      this.monitor = new PollingStatusMonitor(duration, interval, handleGetStatusAsync, handleStatusChanged,
        handleIsEqualStatus, handleError);
    }
    if (!this.monitor.isActive) {
      console.log('DesignerAppLauncher: starting to monitor the bootstrap status...');
      this.hasGottenFirefoxNetworkError = false;
      this.currentBootstrapStatus = null;
      this.monitor.start();
    }
  }

  stopMonitoringBootstrapStatus() {
    if (this.monitor) {
      console.log('DesignerAppLauncher: Stopping bootstrap status monitoring...');
      this.monitor.stop();
    }
  }
}
